import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowLeftOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { redirectToApp } from "../utils/redirectToApp";

const MEETING_LEFT_FROM_BACKGROUND_KEY = "meeting_left_from_background";
const MEETING_REJOIN_PARAMS_KEY = "meeting_rejoin_params";

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const cameFromBackground = useMemo(() => {
    return (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(MEETING_LEFT_FROM_BACKGROUND_KEY) === "true"
    );
  }, []);

  const rejoinParams = useMemo(() => {
    if (!cameFromBackground) return null;
    try {
      const raw = sessionStorage.getItem(MEETING_REJOIN_PARAMS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [cameFromBackground]);

  const handleJoinMeetingAgain = () => {
    sessionStorage.removeItem(MEETING_LEFT_FROM_BACKGROUND_KEY);
    sessionStorage.removeItem(MEETING_REJOIN_PARAMS_KEY);
    if (rejoinParams?.meetingToken) {
      const q = new URLSearchParams({
        meetingToken: rejoinParams.meetingToken,
        ...(rejoinParams.role && { role: rejoinParams.role }),
        ...(rejoinParams.meetingId && { meetingId: rejoinParams.meetingId }),
      });
      navigate(`/meeting?${q.toString()}`, { replace: true });
    }
  };

  const handleBackToApp = () => {
    sessionStorage.removeItem(MEETING_LEFT_FROM_BACKGROUND_KEY);
    sessionStorage.removeItem(MEETING_REJOIN_PARAMS_KEY);
    let role = "";

    role = searchParams.get("role") || "";

    if (!role) {
      const fullSearch = window.location.search;
      const questionMarkCount = (fullSearch.match(/\?/g) || []).length;
      if (questionMarkCount > 1) {
        const normalizedSearch = fullSearch.replace(/\?/g, (match, offset) => {
          return offset === 0 ? match : "&";
        });
        const urlParams = new URLSearchParams(normalizedSearch);
        role = urlParams.get("role") || "";
      } else {
        const urlParams = new URLSearchParams(fullSearch);
        role = urlParams.get("role") || "";
      }
    }

    role = role?.trim() || "";
    redirectToApp(role);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <div className="text-center">
        <img
          src="/assets/icons/IthnainLogoLight.svg"
          alt="Ithnain Logo"
          className="mx-auto mb-8 h-16 w-auto"
        />
        <p className="text-gray-400 text-lg mb-8">
          {cameFromBackground
            ? "The meeting ended when the app went to background."
            : "Redirection to mobile application"}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          {cameFromBackground && rejoinParams?.meetingToken && (
            <Button
              type="primary"
              size="large"
              icon={<VideoCameraOutlined />}
              onClick={handleJoinMeetingAgain}
              className="!bg-green-600 hover:!bg-green-700 !h-12 !px-8 !rounded-lg !font-medium"
            >
              {t("Join meeting again") || "Join meeting again"}
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToApp}
            className="!bg-[#042C43] hover:!bg-[#053a52] !h-12 !px-8 !rounded-lg !font-medium"
          >
            {t("Back to App") || "Back to App"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
